import { useCallback, useEffect, useRef, useState } from "react";
import { Clipboard, Download, Home, Info, LogIn, RefreshCw, Share, Smartphone, WifiOff, X } from "lucide-react";
import { BrandLogo } from "./BrandLogo";
import { Modal } from "./Modal";
import { checkCceHealth, healthEndpoint, isMobileAccess, type HealthCheckResult } from "../lib/health";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

declare global {
  interface Navigator { standalone?: boolean }
  interface Window {
    __cceInstallPrompt?: BeforeInstallPromptEvent | null;
    __ccePwaInstallBound?: boolean;
    __ccePwaUpdate?: (reloadPage?: boolean) => Promise<void>;
  }
}

const DISMISS_KEY = "cce-pwa-install-dismissed";
const DISMISS_DAYS = 7;

export function isStandaloneMode() {
  return window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;
}

export function getMobilePlatform() {
  const agent = navigator.userAgent.toLocaleLowerCase();
  if (/iphone|ipad|ipod/.test(agent)) return "ios" as const;
  if (/android/.test(agent)) return "android" as const;
  return "desktop" as const;
}

export function getPwaClientStatus() {
  return {
    installed: isStandaloneMode(),
    secureContext: window.isSecureContext,
    serviceWorkerSupported: "serviceWorker" in navigator,
    serviceWorkerActive: Boolean(navigator.serviceWorker?.controller),
    platform: getMobilePlatform(),
    version: __APP_VERSION__,
    buildTime: __BUILD_TIME__
  };
}

if (typeof window !== "undefined" && !window.__ccePwaInstallBound) {
  window.__ccePwaInstallBound = true;
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    window.__cceInstallPrompt = event as BeforeInstallPromptEvent;
    window.dispatchEvent(new CustomEvent("cce-install-available"));
  });
  window.addEventListener("appinstalled", () => {
    window.__cceInstallPrompt = null;
    localStorage.removeItem(DISMISS_KEY);
    window.dispatchEvent(new CustomEvent("cce-app-installed"));
  });
}

function recentlyDismissed() {
  const timestamp = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return timestamp > 0 && Date.now() - timestamp < DISMISS_DAYS * 86_400_000;
}

export function InstallAppButton({ force = false, compact = false }: { force?: boolean; compact?: boolean }) {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(() => window.__cceInstallPrompt || null);
  const [installed, setInstalled] = useState(isStandaloneMode);
  const [guide, setGuide] = useState(false);
  const [platform] = useState(getMobilePlatform);

  useEffect(() => {
    const available = () => setPrompt(window.__cceInstallPrompt || null);
    const consumed = () => setPrompt(null);
    const completed = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("cce-install-available", available);
    window.addEventListener("cce-install-consumed", consumed);
    window.addEventListener("cce-app-installed", completed);
    return () => {
      window.removeEventListener("cce-install-available", available);
      window.removeEventListener("cce-install-consumed", consumed);
      window.removeEventListener("cce-app-installed", completed);
    };
  }, []);

  if (installed || (!force && recentlyDismissed()) || (!prompt && platform === "desktop" && !force)) return null;

  const install = async () => {
    if (prompt) {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice.outcome === "dismissed") localStorage.setItem(DISMISS_KEY, String(Date.now()));
      window.__cceInstallPrompt = null;
      window.dispatchEvent(new CustomEvent("cce-install-consumed"));
      setPrompt(null);
      if (choice.outcome === "accepted") setInstalled(true);
      return;
    }
    setGuide(true);
  };

  const retry = () => {
    localStorage.removeItem(DISMISS_KEY);
    setGuide(true);
  };

  return <>
    <button className={compact ? "pwa-install-button" : "button button-primary"} onClick={install} title="Instalar o CCE Gestão neste dispositivo">
      <Download size={compact ? 16 : 18}/><span>{compact ? "Instalar app" : "Instalar CCE Gestão"}</span>
    </button>
    <Modal open={guide} title={platform === "ios" ? "Como instalar no iPhone" : "Instalar aplicativo no celular"} onClose={() => setGuide(false)}>
      <div className="pwa-ios">
        <Smartphone size={34}/>
        {platform === "ios" ? <ol className="install-steps">
          <li><strong>Abra no Safari</strong><span>Use o navegador Safari para acessar o CCE Gestão.</span></li>
          <li><strong>Toque em Compartilhar</strong><span><Share size={16}/> Localize o botão de compartilhamento.</span></li>
          <li><strong>Adicionar à Tela de Início</strong><span><Home size={16}/> Selecione essa opção no menu.</span></li>
          <li><strong>Ative Abrir como App</strong><span>Use essa opção quando ela estiver disponível.</span></li>
          <li><strong>Confirme em Adicionar</strong><span>O ícone aparecerá na tela inicial.</span></li>
        </ol> : <ol className="install-steps">
          <li><strong>Abra o menu do navegador</strong><span>No Chrome ou Edge, toque no menu de três pontos.</span></li>
          <li><strong>Escolha Instalar aplicativo</strong><span>Em alguns aparelhos aparece como Adicionar à tela inicial.</span></li>
          <li><strong>Confirme a instalação</strong><span>Depois, abra pelo ícone do CCE Gestão.</span></li>
        </ol>}
        <p className="pwa-secure-note">Em IP local usando HTTP, alguns navegadores permitem acesso, mas exigem HTTPS para instalar a PWA completa. O acesso HTTP atual continua preservado.</p>
        <div className="modal-actions"><button className="button button-ghost" onClick={retry}>Tentar novamente</button><button className="button button-primary" onClick={() => setGuide(false)}>Entendi</button></div>
      </div>
    </Modal>
  </>;
}

function initialUnavailableResult(): HealthCheckResult {
  return {
    online: false,
    endpoint: healthEndpoint(),
    checkedAt: new Date(),
    networkOnline: navigator.onLine,
    status: "indisponível",
    database: "não verificado",
    errorCode: "HEALTH_NOT_CHECKED",
    errorMessage: "O servidor ainda não respondeu.",
  };
}

function ConnectionPanel({
  result,
  checking,
  onRetry,
  overlay = false,
  onBackToLogin,
}: {
  result: HealthCheckResult;
  checking: boolean;
  onRetry: () => void;
  overlay?: boolean;
  onBackToLogin?: () => void;
}) {
  const [details, setDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const mobile = isMobileAccess();
  const title = mobile ? "Sem conexão com o CCE Gestão" : "Não foi possível conectar ao servidor do CCE Gestão";
  const description = mobile
    ? "Verifique se o notebook está ligado, se o CCE Gestão foi iniciado e se os dois dispositivos estão na mesma rede."
    : "O aplicativo foi aberto, mas o servidor local não está respondendo.";

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(result.endpoint);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    } catch {
      setCopied(false);
    }
  };

  const content = <section className="connection-panel">
    {!overlay && <BrandLogo variant="symbol"/>}
    <WifiOff size={32}/>
    <h1>{title}</h1>
    <p>{description}</p>
    <ul className="connection-guidance">
      {mobile ? <>
        <li>Confirme que o notebook e o celular estão na mesma rede Wi-Fi.</li>
        <li>Abra o atalho “CCE Gestão” no notebook e aguarde a inicialização.</li>
      </> : <>
        <li>Tente abrir novamente o atalho “CCE Gestão”.</li>
        <li>Consulte o arquivo <code>logs\startup.log</code> se o servidor não iniciar.</li>
      </>}
    </ul>
    <div className="connection-actions">
      <button className="button button-primary" disabled={checking} onClick={onRetry}>
        {checking ? <span className="spinner white"/> : <RefreshCw size={17}/>} Tentar novamente
      </button>
      <button className="button button-secondary" onClick={() => setDetails((value) => !value)}>
        <Info size={17}/> {details ? "Ocultar diagnóstico" : "Ver diagnóstico"}
      </button>
      <button className="button button-secondary" onClick={() => void copyAddress()}>
        <Clipboard size={17}/> {copied ? "Endereço copiado" : "Copiar endereço"}
      </button>
      {onBackToLogin && <button className="button button-ghost" onClick={onBackToLogin}><LogIn size={17}/> Voltar ao login</button>}
    </div>
    {details && <dl className="connection-diagnostics">
      <div><dt>Endereço consultado</dt><dd>{result.endpoint}</dd></div>
      <div><dt>Data e hora</dt><dd>{result.checkedAt.toLocaleString("pt-BR")}</dd></div>
      <div><dt>Código do erro</dt><dd>{result.errorCode || "SEM_ERRO"}</dd></div>
      <div><dt>HTTP</dt><dd>{result.httpStatus ?? "sem resposta"}</dd></div>
      <div><dt>Health check</dt><dd>{result.status}</dd></div>
      <div><dt>Banco</dt><dd>{result.database}</dd></div>
      <div><dt>Rede do dispositivo</dt><dd>{result.networkOnline ? "online" : "offline"}</dd></div>
      <div><dt>Versão do servidor</dt><dd>{result.version || "não informada"}</dd></div>
      {result.errorMessage && <div className="wide"><dt>Detalhe</dt><dd>{result.errorMessage}</dd></div>}
    </dl>}
  </section>;

  if (overlay) return <div className="connection-overlay" role="alertdialog" aria-modal="true" aria-label={title}>{content}</div>;
  return <main className="offline-screen">{content}</main>;
}

export function OfflineScreen({
  onRetry,
  onBackToLogin,
}: {
  onRetry: () => void | Promise<void>;
  onBackToLogin?: () => void;
}) {
  const [result, setResult] = useState<HealthCheckResult>(initialUnavailableResult);
  const [trying, setTrying] = useState(false);

  const retry = useCallback(async () => {
    setTrying(true);
    const next = await checkCceHealth();
    setResult(next);
    if (next.online) await onRetry();
    setTrying(false);
  }, [onRetry]);

  useEffect(() => {
    const initialCheck = window.setTimeout(() => void retry(), 0);
    return () => window.clearTimeout(initialCheck);
  }, [retry]);
  return <ConnectionPanel result={result} checking={trying} onRetry={() => void retry()} onBackToLogin={onBackToLogin}/>;
}

export function PwaInstall() {
  const [updateWorker, setUpdateWorker] = useState<null | ((reloadPage?: boolean) => Promise<void>)>(() => window.__ccePwaUpdate || null);
  const [health, setHealth] = useState<HealthCheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [installHint, setInstallHint] = useState(() => getMobilePlatform() !== "desktop" && !isStandaloneMode() && !recentlyDismissed());
  const consecutiveHealthFailures = useRef(0);

  const checkServer = useCallback(async () => {
    setChecking(true);
    const next = await checkCceHealth();
    if (next.online) {
      consecutiveHealthFailures.current = 0;
      setHealth(next);
    } else {
      consecutiveHealthFailures.current += 1;
      if (!next.networkOnline || consecutiveHealthFailures.current >= 2) setHealth(next);
    }
    setChecking(false);
  }, []);

  useEffect(() => {
    const initialCheck = window.setTimeout(() => void checkServer(), 0);
    const onUpdate = (event: Event) => setUpdateWorker(() => (event as CustomEvent<() => Promise<void>>).detail);
    const onOnline = () => void checkServer();
    const onOffline = () => {
      consecutiveHealthFailures.current = 2;
      setHealth(initialUnavailableResult());
    };
    const onInstallAvailable = () => setInstallHint(getMobilePlatform() !== "desktop" && !recentlyDismissed());
    const onInstallConsumed = () => setInstallHint(false);
    window.addEventListener("cce-pwa-update", onUpdate);
    window.addEventListener("cce-install-available", onInstallAvailable);
    window.addEventListener("cce-install-consumed", onInstallConsumed);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const timer = window.setInterval(() => void checkServer(), 30_000);
    return () => {
      window.removeEventListener("cce-pwa-update", onUpdate);
      window.removeEventListener("cce-install-available", onInstallAvailable);
      window.removeEventListener("cce-install-consumed", onInstallConsumed);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.clearTimeout(initialCheck);
      window.clearInterval(timer);
    };
  }, [checkServer]);

  const dismissInstall = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setInstallHint(false);
  };

  return <>
    <InstallAppButton compact/>
    {installHint && <div className="pwa-banner pwa-install-hint"><Smartphone size={19}/><span>Instale o CCE Gestão no seu celular para acessar o sistema como um aplicativo.</span><InstallAppButton/><button className="pwa-banner-close" onClick={dismissInstall} aria-label="Lembrar depois"><X size={16}/></button></div>}
    {health && !health.online && <ConnectionPanel result={health} checking={checking} onRetry={() => void checkServer()} overlay/>}
    {updateWorker && <div className="pwa-banner"><RefreshCw size={19}/><span><strong>Nova versão disponível</strong>O CCE Gestão recebeu melhorias. Atualize quando terminar o formulário em andamento.</span><button onClick={() => updateWorker(true)}>Atualizar agora</button><button className="pwa-banner-close" onClick={() => setUpdateWorker(null)} aria-label="Atualizar depois">Depois</button></div>}
  </>;
}
